// Cinematic guided tour — the v2 redesign.
//
// Layers (bottom to top):
//   1. Backdrop  — dark gradient + animated noise grain + page blur
//   2. Spotlight — radial light source emanating from the target,
//                  conic-gradient rotating rays, pulsing halo ring
//   3. Particles — drifting sage particles flowing toward spotlight
//   4. Card      — narration with integrated speaker orb + Roman-numeral
//                  step indicator, 3D perspective entry
//   5. Ribbon    — thin progress bar at the top of the viewport
//
// Special intro stage at step 0 — full-screen orchestrated typography
// with the orb at 240px, no spotlight target. Sets the experience apart
// from "tab through tooltips."
//
// Designed to feel like a scene change, not a settings panel.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { TOUR_STEPS, type TourStep } from "./tourSteps";

// ─── localStorage keys ──────────────────────────────────────────────────
const LS_COMPLETED_AT = "njrc.tour.completedAt";
const LS_RUN_COUNT = "njrc.tour.runCount";
const LS_DISMISSED_AT = "njrc.tour.dismissedAt";

// ─── Color palette ──────────────────────────────────────────────────────
// Mirrors the live app's warm-cream-on-dark palette (NOT a separate cool
// blue identity). Same Fraunces display + warm sage/amber/rose accents
// the rest of the app uses, so the tour feels like part of the same
// product rather than a stranger.
const C = {
  text: "#F2EDDF",      // warm cream — main text
  muted: "#C4C2B5",     // muted cream — body
  dim: "#8E8F84",       // dim cream — labels
  // App accents (matched from index.css)
  sage: "#16A34A",
  sageHi: "#22C55E",
  sageDeep: "#0F7C39",
  amber: "#c47a32",
  amberHi: "#e8a050",
  rose: "#E63858",
  roseHi: "#FB7185",
  gold: "#D97706",
  goldHi: "#FBBF24",
  blue: "#1D4ED8",
  blueHi: "#60A5FA",
  // Surfaces
  void: "#0a0c0f",
  bg: "#16181A",
  bgCard: "#1F2226",
  glass: "rgba(31,34,38,0.85)",
  glassHi: "rgba(39,42,47,0.92)",
  line: "rgba(242,237,223,0.08)",
  lineHi: "rgba(242,237,223,0.18)",
} as const;

// ─── Public API ─────────────────────────────────────────────────────────

export function getTourStats(): {
  completedAt: string | null;
  runCount: number;
  dismissedAt: string | null;
} {
  try {
    return {
      completedAt: localStorage.getItem(LS_COMPLETED_AT),
      runCount: parseInt(localStorage.getItem(LS_RUN_COUNT) ?? "0", 10) || 0,
      dismissedAt: localStorage.getItem(LS_DISMISSED_AT),
    };
  } catch {
    return { completedAt: null, runCount: 0, dismissedAt: null };
  }
}

export function startTour(): void {
  window.dispatchEvent(new CustomEvent("njrc:tour:start"));
}

// ─── Component ──────────────────────────────────────────────────────────

export function Tour(): JSX.Element | null {
  const [active, setActive] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [autoLaunchScheduled, setAutoLaunchScheduled] = useState(false);
  // Direction of last step change — drives the entry animation. +1 forward, -1 back.
  const [direction, setDirection] = useState<1 | -1>(1);
  const navigate = useNavigate();
  const location = useLocation();

  // Imperative trigger
  useEffect(() => {
    const onStart = () => {
      setStepIdx(0);
      setDirection(1);
      setActive(true);
      try {
        const cur = parseInt(localStorage.getItem(LS_RUN_COUNT) ?? "0", 10) || 0;
        localStorage.setItem(LS_RUN_COUNT, String(cur + 1));
      } catch {
        // ignore
      }
    };
    window.addEventListener("njrc:tour:start", onStart);
    return () => window.removeEventListener("njrc:tour:start", onStart);
  }, []);

  // First-run auto-launch
  useEffect(() => {
    if (autoLaunchScheduled) return;
    setAutoLaunchScheduled(true);
    const stats = getTourStats();
    if (stats.completedAt || stats.dismissedAt) return;
    const t = setTimeout(() => startTour(), 1200);
    return () => clearTimeout(t);
  }, [autoLaunchScheduled]);

  // Step transition. The slideshow tour doesn't try to highlight live
  // page elements anymore — every step is a self-contained slide on a
  // deep backdrop with its own stylized illustration. We still navigate
  // to the route the step belongs to so any return-to-app moment lands
  // the user in the right place.
  useEffect(() => {
    if (!active) return;
    const step = TOUR_STEPS[stepIdx];
    if (!step) return;
    let cancelled = false;
    void (async () => {
      if (step.route && location.pathname !== step.route) {
        navigate(step.route);
      }
      if (step.beforeShow && !cancelled) {
        try { await step.beforeShow(); } catch { /* best-effort */ }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, stepIdx]);

  // Keyboard
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismissTour();
      else if (e.key === "ArrowRight" || e.key === "Enter") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, stepIdx]);

  const next = useCallback(() => {
    if (stepIdx >= TOUR_STEPS.length - 1) {
      complete();
      return;
    }
    setDirection(1);
    setStepIdx((i) => i + 1);
  }, [stepIdx]);

  const prev = useCallback(() => {
    if (stepIdx === 0) return;
    setDirection(-1);
    setStepIdx((i) => i - 1);
  }, [stepIdx]);

  const complete = useCallback(() => {
    try {
      localStorage.setItem(LS_COMPLETED_AT, new Date().toISOString());
    } catch {
      // ignore
    }
    setActive(false);
    setStepIdx(0);
  }, []);

  const dismissTour = useCallback(() => {
    try {
      localStorage.setItem(LS_DISMISSED_AT, new Date().toISOString());
    } catch {
      // ignore
    }
    setActive(false);
    setStepIdx(0);
  }, []);

  if (!active) return null;
  const step = TOUR_STEPS[stepIdx];
  if (!step) return null;

  return (
    <>
      <TourStyles />
      {/* Every tour step gets the deep, blurred backdrop. The slideshow
          owns the screen — we don't try to overlay anything on the live
          app. Way more reliable than DOM-tracking spotlights. */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9990,
          pointerEvents: "auto",
          animation: "tour-stage-in 320ms cubic-bezier(0.2, 0.8, 0.2, 1)",
        }}
      >
        <TourBackdrop variant="deep" />
        {step.kind === "intro" ? (
          <IntroStage step={step} idx={stepIdx} total={TOUR_STEPS.length} onNext={next} onDismiss={dismissTour} />
        ) : step.kind === "outro" ? (
          <OutroStage step={step} idx={stepIdx} total={TOUR_STEPS.length} onNext={next} onDismiss={dismissTour} />
        ) : (
          <TeachingStage
            step={step}
            idx={stepIdx}
            total={TOUR_STEPS.length}
            direction={direction}
            onNext={next}
            onPrev={prev}
            onDismiss={dismissTour}
          />
        )}
      </div>

      <ProgressRibbon idx={stepIdx} total={TOUR_STEPS.length} />
    </>
  );
}

// ─── Backdrop layer ─────────────────────────────────────────────────────
// Deep dark base + animated noise grain + page blur. Two intensities:
// "regular" for spotlight steps, "deep" for full-screen narrative steps
// where we want the underlying app to disappear completely.

function TourBackdrop({ variant: _variant }: { variant: "deep" }): JSX.Element {
  // Single backdrop variant — full-screen deep blur + ambient sage-blue
  // gradient. The tour is a slideshow now; we always want the live page
  // to fully recede behind the storytelling content.
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: `radial-gradient(circle at 50% 35%, rgba(242,237,223,0.04), transparent 60%), rgba(8,12,18,0.92)`,
        backdropFilter: `blur(14px) saturate(0.95)`,
        WebkitBackdropFilter: `blur(14px) saturate(0.95)`,
        pointerEvents: "none",
        animation: "tour-backdrop-in 600ms ease-out",
      }}
    >
      <svg
        width="100%"
        height="100%"
        style={{
          position: "absolute",
          inset: 0,
          opacity: 0.06,
          mixBlendMode: "overlay",
          pointerEvents: "none",
        }}
        aria-hidden="true"
      >
        <filter id="tour-noise">
          <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" stitchTiles="stitch" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 1
                    0 0 0 0 1
                    0 0 0 0 1
                    0 0 0 0.9 0"
          />
        </filter>
        <rect width="100%" height="100%" filter="url(#tour-noise)" />
      </svg>
    </div>
  );
}

// ─── Intro Stage ────────────────────────────────────────────────────────
// The cinematic opening. Full-screen black with the orb large + center,
// orchestrated typography fading in line by line. Sets the tone before
// any spotlights happen.

function IntroStage(props: {
  step: TourStep;
  idx: number;
  total: number;
  onNext: () => void;
  onDismiss: () => void;
}): JSX.Element {
  const step = props.step;
  // Word count of the title — used to stagger the subtitle and CTA
  // entry animations so they feel sequenced relative to the headline
  // landing rather than appearing on a fixed timeline.
  const titleWords = step.title.split(/\s+/).length;
  const titleSettleMs = 1100 + titleWords * 110 + 700;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        // Vertical padding scales with height so very short viewports
        // (e.g., 600px netbooks) don't waste fixed 32px on each side.
        paddingTop: "min(32px, 4vh)",
        paddingBottom: "min(32px, 4vh)",
        paddingLeft: 24,
        paddingRight: 24,
        // Safety net: if content still doesn't fit (e.g., 800x500
        // dialog window), allow scroll inside the intro rather than
        // clipping the CTA — the user can always reach the button.
        overflowY: "auto",
        // Re-enable pointer events — the parent wrapper opts out so the
        // page underneath stays clickable, so each foreground surface
        // (this intro stage, narration cards) opts back in explicitly.
        pointerEvents: "auto",
        // Slowly drifting ambient warm gradient — atmospheric movement
        // behind the cinematic intro typography. Plus the page-level
        // fade-in. Both compose into a single CSS animation list.
        background:
          "radial-gradient(ellipse 80% 60% at 30% 40%, rgba(196,122,50,0.06), transparent 60%), radial-gradient(ellipse 70% 50% at 70% 60%, rgba(22,163,74,0.04), transparent 60%)",
        backgroundSize: "200% 200%, 200% 200%",
        animation:
          "tour-intro-fade-in 600ms ease-out, tour-ambient-drift 24s ease-in-out infinite",
      }}
    >
      {/* Big orb at center, drifts in. Sized to leave room for the
          headline + subtitle + CTAs in a 900px-tall laptop viewport;
          uses min(vh, fixed) so it scales down gracefully on shorter
          windows but doesn't dominate on tall ones. Floor of 90px so
          the orb stays visually present even on tiny dialogs. */}
      <div
        style={{
          marginBottom: "min(24px, 2.5vh)",
          animation: "tour-intro-orb-in 1400ms cubic-bezier(0.2, 0.8, 0.2, 1) both",
        }}
      >
        <BigIntroOrb size={Math.max(90, Math.min(180, Math.round(Math.min(window.innerHeight, 900) * 0.20)))} />
      </div>

      {/* Eyebrow — tiny caps */}
      <div
        style={{
          fontFamily: "var(--font-body)",
          fontSize: 11,
          letterSpacing: 3,
          textTransform: "uppercase",
          color: C.sage,
          fontWeight: 700,
          marginBottom: "min(18px, 2vh)",
          animation: "tour-intro-line 700ms cubic-bezier(0.2, 0.8, 0.2, 1) 600ms both",
        }}
      >
        {step.eyebrow ?? "NJRC Legal Services Tool"}
      </div>

      {/* Hero headline — word-by-word animation. Each word fades up,
          un-blurs, and lands with a tiny stagger so the whole line
          composes itself like a film title sequence. */}
      <h1
        style={{
          fontFamily: "var(--font-display)",
          // Triple-clamp: also bound by viewport HEIGHT (8vh) so a
          // short window doesn't get a giant headline. At 600h that
          // caps fontSize at 48px instead of 72.
          fontSize: "min(clamp(34px, 5.2vw, 64px), 8vh)",
          fontWeight: 400,
          fontVariationSettings: '"opsz" 144, "SOFT" 60',
          lineHeight: 1.04,
          letterSpacing: -0.5,
          color: C.text,
          margin: 0,
          marginBottom: "min(16px, 2vh)",
          textAlign: "center",
          maxWidth: 1100,
        }}
      >
        {step.title.split(/\s+/).map((word, i, arr) => (
          <span
            key={`${props.idx}-w${i}`}
            style={{
              display: "inline-block",
              animation: `tour-intro-word 700ms cubic-bezier(0.2, 0.8, 0.2, 1) ${1100 + i * 110}ms both`,
              marginRight: i < arr.length - 1 ? "0.28em" : 0,
            }}
          >
            {word}
          </span>
        ))}
      </h1>

      {/* Subtitle / lines */}
      <div
        style={{
          fontFamily: "var(--font-display)",
          fontStyle: "italic",
          fontSize: "clamp(16px, 1.8vw, 20px)",
          fontWeight: 400,
          fontVariationSettings: '"opsz" 24, "SOFT" 50',
          lineHeight: 1.5,
          color: C.muted,
          maxWidth: 760,
          textAlign: "center",
          margin: 0,
          marginBottom: "min(32px, 4vh)",
          letterSpacing: -0.1,
          animation: "tour-intro-line 900ms cubic-bezier(0.2, 0.8, 0.2, 1) 1700ms both",
        }}
      >
        {renderMarkdown(step.body)}
      </div>

      {/* CTAs */}
      <div
        style={{
          display: "flex",
          gap: 14,
          alignItems: "center",
          flexWrap: "wrap",
          justifyContent: "center",
          animation: "tour-intro-line 700ms cubic-bezier(0.2, 0.8, 0.2, 1) 2400ms both",
        }}
      >
        <button
          onClick={props.onNext}
          style={{
            background: `linear-gradient(135deg, ${C.sage}, ${C.sageDeep})`,
            color: "#0a1310",
            border: "none",
            borderRadius: 999,
            padding: "16px 32px",
            fontFamily: "var(--font-body)",
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: 0.6,
            cursor: "pointer",
            boxShadow:
              "0 14px 38px rgba(22,163,74,0.40), inset 0 1px 0 rgba(255,255,255,0.22)",
          }}
        >
          Take the 3-minute tour →
        </button>
        <button
          onClick={props.onDismiss}
          style={{
            background: "transparent",
            color: C.dim,
            border: "none",
            fontFamily: "var(--font-body)",
            fontSize: 13,
            fontWeight: 500,
            letterSpacing: 0.3,
            cursor: "pointer",
            padding: "10px 14px",
          }}
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}

function OutroStage(props: {
  step: TourStep;
  idx: number;
  total: number;
  onNext: () => void;
  onDismiss: () => void;
}): JSX.Element {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 32,
        pointerEvents: "auto",
        animation: "tour-intro-fade-in 600ms ease-out",
      }}
    >
      <div
        style={{
          marginBottom: 30,
          animation: "tour-intro-orb-in 1400ms cubic-bezier(0.2, 0.8, 0.2, 1) both",
        }}
      >
        <BigIntroOrb size={160} />
      </div>
      <div
        style={{
          fontFamily: "var(--font-body)",
          fontSize: 11,
          letterSpacing: 3,
          textTransform: "uppercase",
          color: C.sage,
          fontWeight: 700,
          marginBottom: 18,
          animation: "tour-intro-line 700ms ease-out 500ms both",
        }}
      >
        {props.step.eyebrow ?? "You're ready"}
      </div>
      <h1
        style={{
          fontFamily: "var(--font-display)",
          fontSize: "min(clamp(34px, 5.4vw, 64px), 8vh)",
          fontWeight: 400,
          fontVariationSettings: '"opsz" 144, "SOFT" 60',
          lineHeight: 1.04,
          letterSpacing: -0.4,
          color: C.text,
          margin: 0,
          marginBottom: 18,
          textAlign: "center",
          maxWidth: 900,
        }}
      >
        {props.step.title.split(/\s+/).map((word, i, arr) => (
          <span
            key={`outro-w${i}`}
            style={{
              display: "inline-block",
              animation: `tour-intro-word 700ms cubic-bezier(0.2, 0.8, 0.2, 1) ${900 + i * 110}ms both`,
              marginRight: i < arr.length - 1 ? "0.28em" : 0,
            }}
          >
            {word}
          </span>
        ))}
      </h1>
      <div
        style={{
          fontFamily: "var(--font-body)",
          fontSize: 16,
          lineHeight: 1.55,
          color: C.muted,
          maxWidth: 580,
          textAlign: "center",
          margin: 0,
          marginBottom: 36,
          animation: "tour-intro-line 800ms ease-out 1400ms both",
        }}
      >
        {renderMarkdown(props.step.body)}
      </div>
      <button
        onClick={props.onNext}
        style={{
          background: `linear-gradient(135deg, ${C.sage}, ${C.sageDeep})`,
          color: "#0a1310",
          border: "none",
          borderRadius: 999,
          padding: "14px 28px",
          fontFamily: "var(--font-body)",
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: 0.6,
          cursor: "pointer",
          boxShadow: "0 14px 38px rgba(22,163,74,0.40)",
          animation: "tour-intro-line 700ms ease-out 1900ms both",
        }}
      >
        Get to work ✦
      </button>
    </div>
  );
}

// ─── Teaching Stage ─────────────────────────────────────────────────────
// The slideshow body. Each tour step (other than intro/outro) renders
// here: persistent breathing AI orb at top, eyebrow + word-by-word
// title, body paragraph, a stylized illustration card per step that
// evokes the feature being taught, and Back/Next controls.
//
// We used to try to spotlight live DOM elements with cutouts and
// rotating arcs, but DOM-tracking on a scrolling page was hopelessly
// fragile. The slideshow approach owns the screen completely — fully
// reliable, and the illustrations let us teach features with motion
// that the live UI doesn't have.

// TeachingStage = persistent chrome (orb + ambient gradient + nav row)
// + a keyed inner SlideContent that swaps per step. Critically, the
// outer wrapper is NOT keyed by step idx — that's why the orb keeps
// breathing across slides instead of restarting on every Continue.
function TeachingStage(props: {
  step: TourStep;
  idx: number;
  total: number;
  direction: 1 | -1;
  onNext: () => void;
  onPrev: () => void;
  onDismiss: () => void;
}): JSX.Element {
  const orbSize = Math.max(72, Math.min(110, Math.round(Math.min(window.innerHeight, 900) * 0.12)));
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        paddingTop: "min(24px, 3vh)",
        paddingBottom: "min(24px, 3vh)",
        paddingLeft: 24,
        paddingRight: 24,
        overflowY: "auto",
        pointerEvents: "auto",
        background:
          "radial-gradient(ellipse 80% 60% at 30% 40%, rgba(196,122,50,0.05), transparent 60%), radial-gradient(ellipse 70% 50% at 70% 60%, rgba(22,163,74,0.04), transparent 60%)",
        backgroundSize: "200% 200%, 200% 200%",
        animation: "tour-ambient-drift 24s ease-in-out infinite",
      }}
    >
      {/* Persistent AI orb — same particle-system orb as the intro.
          NOT keyed, so it keeps breathing across all teaching slides. */}
      <div style={{ marginBottom: "min(14px, 1.6vh)", flexShrink: 0 }}>
        <BigIntroOrb size={orbSize} />
      </div>

      {/* Per-step content — keyed so it remounts and re-animates each
          step. Only this block fades, NOT the orb / backdrop / nav. */}
      <SlideContent step={props.step} idx={props.idx} key={props.idx} />

      <div style={{ flex: 1 }} />

      {/* Nav controls — persistent, just labels update */}
      <NavBar
        idx={props.idx}
        total={props.total}
        onPrev={props.onPrev}
        onNext={props.onNext}
        onDismiss={props.onDismiss}
      />
    </div>
  );
}

function SlideContent({ step, idx }: { step: TourStep; idx: number }): JSX.Element {
  const titleWords = step.title.split(/\s+/).length;
  const titleSettleMs = 400 + titleWords * 70 + 200;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        animation: "tour-slide-fade-in 320ms ease-out",
      }}
    >
      {/* Eyebrow */}
      <div
        style={{
          fontFamily: "var(--font-display)",
          fontStyle: "italic",
          fontVariationSettings: '"opsz" 24, "SOFT" 70',
          fontSize: 12,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: C.amberHi,
          fontWeight: 500,
          marginBottom: "min(10px, 1.2vh)",
        }}
      >
        {step.eyebrow ?? `Chapter ${idx + 1}`}
      </div>

      {/* Title — word-by-word reveal */}
      <h2
        style={{
          fontFamily: "var(--font-display)",
          fontSize: "min(clamp(26px, 3.8vw, 44px), 5.8vh)",
          fontWeight: 400,
          fontVariationSettings: '"opsz" 144, "SOFT" 70',
          lineHeight: 1.08,
          letterSpacing: -0.4,
          color: C.text,
          margin: 0,
          marginBottom: "min(12px, 1.6vh)",
          textAlign: "center",
          maxWidth: 820,
        }}
      >
        {step.title.split(/\s+/).map((word, i, arr) => (
          <span
            key={`${idx}-w${i}`}
            style={{
              display: "inline-block",
              animation: `tour-intro-word 500ms cubic-bezier(0.2, 0.8, 0.2, 1) ${300 + i * 70}ms both`,
              marginRight: i < arr.length - 1 ? "0.28em" : 0,
            }}
          >
            {word}
          </span>
        ))}
      </h2>

      {/* Body — Fraunces serif body, slightly larger than before, with
          inline **bold** and *italic* parsing for emphasis. */}
      <div
        style={{
          fontFamily: "var(--font-display)",
          fontVariationSettings: '"opsz" 24, "SOFT" 60',
          fontSize: "clamp(15px, 1.5vw, 18px)",
          lineHeight: 1.55,
          color: C.muted,
          maxWidth: 620,
          textAlign: "center",
          margin: 0,
          marginBottom: "min(22px, 2.8vh)",
          animation: `tour-intro-line 600ms cubic-bezier(0.2, 0.8, 0.2, 1) ${titleSettleMs}ms both`,
        }}
      >
        {renderMarkdown(step.body)}
      </div>

      {/* Illustration */}
      <div
        style={{
          marginBottom: "min(20px, 2.5vh)",
          animation: `tour-intro-line 700ms cubic-bezier(0.2, 0.8, 0.2, 1) ${titleSettleMs + 200}ms both`,
        }}
      >
        <Illustration kind={step.illustration ?? step.id} />
      </div>
    </div>
  );
}

// Persistent nav bar — labels update with idx but the row itself doesn't
// re-animate. Lives under TeachingStage.
function NavBar(props: { idx: number; total: number; onPrev: () => void; onNext: () => void; onDismiss: () => void }): JSX.Element {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, flexShrink: 0 }}>
      <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
        <button
          onClick={props.onPrev}
          disabled={props.idx === 0}
          style={{
            background: "transparent",
            color: props.idx === 0 ? "rgba(242,237,223,0.20)" : C.dim,
            border: `1px solid ${props.idx === 0 ? C.line : C.lineHi}`,
            borderRadius: 999,
            padding: "11px 20px",
            fontFamily: "var(--font-body)",
            fontSize: 13,
            fontWeight: 500,
            letterSpacing: 0.3,
            cursor: props.idx === 0 ? "default" : "pointer",
            transition: "color 200ms ease, border-color 200ms ease",
          }}
        >
          ← Back
        </button>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: C.dim,
            letterSpacing: 1.4,
            fontVariantNumeric: "tabular-nums",
            padding: "0 6px",
          }}
        >
          {props.idx + 1} / {props.total}
        </div>
        <button
          onClick={props.onNext}
          style={{
            background: `linear-gradient(135deg, ${C.sage}, ${C.sageDeep})`,
            color: "#0a1310",
            border: "none",
            borderRadius: 999,
            padding: "11px 26px",
            fontFamily: "var(--font-body)",
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: 0.5,
            cursor: "pointer",
            boxShadow: "0 10px 28px rgba(22,163,74,0.36), inset 0 1px 0 rgba(255,255,255,0.22)",
          }}
        >
          {props.idx === props.total - 1 ? "Finish" : "Continue →"}
        </button>
      </div>
      <button
        onClick={props.onDismiss}
        style={{
          background: "transparent",
          color: C.dim,
          border: "none",
          fontFamily: "var(--font-body)",
          fontSize: 12,
          letterSpacing: 0.3,
          cursor: "pointer",
          opacity: 0.6,
          padding: 4,
        }}
      >
        Skip the rest
      </button>
    </div>
  );
}

// Lightweight markdown — supports **bold** and *italic* inside an
// otherwise plain string. Only what we need for tour copy; not a full
// parser. Bold/italic let us call out things like the route name or
// the menu the user should click.
function renderMarkdown(text: string): JSX.Element[] {
  const out: JSX.Element[] = [];
  const re = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIdx) out.push(<span key={key++}>{text.slice(lastIdx, m.index)}</span>);
    const tok = m[0];
    if (tok.startsWith("**")) {
      out.push(<strong key={key++} style={{ color: C.text, fontWeight: 700 }}>{tok.slice(2, -2)}</strong>);
    } else {
      out.push(<em key={key++} style={{ color: C.amberHi, fontStyle: "italic" }}>{tok.slice(1, -1)}</em>);
    }
    lastIdx = re.lastIndex;
  }
  if (lastIdx < text.length) out.push(<span key={key++}>{text.slice(lastIdx)}</span>);
  return out;
}

// ─── Illustrations ─────────────────────────────────────────────────────
// One stylized art panel per teaching step. CSS/React only — no
// screenshots — so they age gracefully as the live UI evolves.
// Each illustration evokes the page being discussed without trying to
// be a literal copy. All share the cool blue + sage palette and a
// subtle ambient pulse so they feel like part of the same world as
// the AI orb.

function Illustration({ kind }: { kind: string }): JSX.Element {
  switch (kind) {
    case "capabilities":      return <CapabilitiesArt />;
    case "nav-overview":      return <NavOverviewArt />;
    case "cmd-k":             return <CmdKArt />;
    case "ai-orb":            return <AiOrbArt />;
    case "today":             return <TodayBriefArt />;
    case "lookup":            return <LookupFormArt />;
    case "people-list":       return <PeopleListArt />;
    case "person-hero":       return <PersonHeroArt />;
    case "person-records":    return <PersonRecordsArt />;
    case "actions-button-spotlight":
    case "actions":           return <ActionsMenuArt />;
    case "actions-menu-open": return <ActionsMenuArt />;
    case "stats":             return <StatsArt />;
    case "how-to":            return <HowToArt />;
    default:                  return <GenericArt />;
  }
}

// Big-picture capabilities grid — six tiles each evoking one thing the
// app does end-to-end. Subtle pulse on each tile for liveness.
function CapabilitiesArt(): JSX.Element {
  const tiles = [
    { icon: "⚖", label: "Court records",   sub: "Auto-pulled from every NJ court", color: C.amberHi },
    { icon: "🛂", label: "MVC + DOC",       sub: "License + incarceration history", color: C.gold },
    { icon: "🎙", label: "Live meetings",   sub: "AI transcription + auto-fill",   color: C.sageHi },
    { icon: "✉", label: "Court letters",   sub: "Drafted from the participant",   color: C.blueHi },
    { icon: "📄", label: "Resumes + forms", sub: "DO-21, expungements, handouts", color: C.amber },
    { icon: "📦", label: "Packets",         sub: "One-click downloads",            color: C.roseHi },
  ];
  return (
    <div style={{ ...ART_CARD, padding: "18px 18px" }}>
      <div style={ART_LABEL}>What this tool actually does</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 8 }}>
        {tiles.map((t, i) => (
          <div
            key={t.label}
            style={{
              padding: "12px 10px",
              borderRadius: 10,
              background: "rgba(8,12,20,0.55)",
              border: `1px solid ${C.line}`,
              animation: `tour-art-slide 500ms cubic-bezier(0.2, 0.8, 0.2, 1) ${i * 90}ms both`,
              minHeight: 78,
            }}
          >
            <div style={{ fontSize: 18, marginBottom: 4, color: t.color }}>{t.icon}</div>
            <div style={{ fontFamily: "var(--font-display)", fontSize: 13, color: C.text, fontWeight: 500, fontVariationSettings: '"opsz" 24, "SOFT" 60', lineHeight: 1.15 }}>{t.label}</div>
            <div style={{ fontFamily: "var(--font-body)", fontSize: 10, color: C.dim, marginTop: 2, lineHeight: 1.3 }}>{t.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Nav overview — mockup of the actual current top nav row. Italic
// Fraunces verbs on the left, then a divider, then the right cluster
// (⌘K, Tour, Ask Jack orb, avatar). The active tab gets the
// cream-gold underbar that matches the live nav. Mirrors what the
// user is looking at on /today behind the slideshow.
function NavOverviewArt(): JSX.Element {
  const verbs = [
    { label: "Today",  active: true,  ref: false },
    { label: "Lookup", active: false, ref: false },
    { label: "People", active: false, ref: false },
    { label: "Stats",  active: false, ref: false },
    // How To gets the cream-gold accent in the real nav — flag it here.
    { label: "How To", active: false, ref: true },
  ];
  return (
    <div style={{ ...ART_CARD, padding: "18px 18px" }}>
      <div style={ART_LABEL}>Top navigation</div>
      {/* Fake nav bar — matches the live single-row layout. */}
      <div
        style={{
          display: "flex",
          gap: 14,
          padding: "12px 14px",
          borderRadius: 10,
          background: "rgba(8,12,20,0.55)",
          border: `1px solid ${C.line}`,
          marginTop: 6,
          marginBottom: 14,
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "nowrap",
          overflowX: "auto",
        }}
      >
        {/* Left cluster — italic verbs */}
        <div style={{ display: "flex", gap: 18, alignItems: "center", flexShrink: 0 }}>
          {verbs.map((v) => (
            <div
              key={v.label}
              style={{
                position: "relative",
                fontFamily: "var(--font-display)",
                fontStyle: "italic",
                fontSize: 14,
                fontWeight: v.active ? 600 : 500,
                fontVariationSettings: '"opsz" 36, "SOFT" 50',
                letterSpacing: "-0.005em",
                color: v.active ? C.text : v.ref ? "#D6BE7A" : C.dim,
                paddingBottom: 4,
              }}
            >
              {v.label}
              {v.active && (
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: -1,
                    height: 1.5,
                    background: "#D6BE7A",
                    borderRadius: 1,
                  }}
                />
              )}
            </div>
          ))}
        </div>
        {/* Right cluster — ⌘K, Tour, Jack orb, avatar */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0 }}>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              background: "linear-gradient(90deg, #4DA8FF, #7C5BFF, #FF5BD0, #FFB85B, #2EE6C9)",
              backgroundSize: "220% 100%",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              animation: "tour-art-shimmer 5s linear infinite",
            }}
          >
            ⌘K
          </div>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "#2EE6C9",
            }}
          >
            Tour
          </div>
          {/* Mini Jack orb — tinted blue dot with glow */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "3px 8px 3px 5px",
              borderRadius: 999,
              background: "rgba(77, 168, 255, 0.10)",
              border: "1px solid rgba(77, 168, 255, 0.40)",
              color: "#7CC2FF",
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: "radial-gradient(circle at 30% 30%, #BFE2FF, #4DA8FF 60%, #1D4ED8)",
                boxShadow: "0 0 8px rgba(77,168,255,0.6)",
                animation: "tour-art-pulse 2.4s ease-in-out infinite",
              }}
            />
            Jack
          </div>
          {/* Avatar dot */}
          <div
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #8C1F2F, #6B1825)",
              border: "1px solid rgba(255,255,255,0.18)",
              flexShrink: 0,
            }}
          />
        </div>
      </div>
      {/* Per-tab descriptions */}
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {[
          { label: "Today",  desc: "Morning brief + AI commentary" },
          { label: "Lookup", desc: "Add a new participant" },
          { label: "People", desc: "Your full roster, color-coded" },
          { label: "Stats",  desc: "Charts, maps, demographics" },
          { label: "How To", desc: "Manual + Ask Jack chatbot" },
        ].map((t, i) => (
          <div
            key={t.label}
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 10,
              padding: "5px 0",
              animation: `tour-art-slide 500ms cubic-bezier(0.2, 0.8, 0.2, 1) ${i * 90}ms both`,
            }}
          >
            <div style={{ fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: 14, color: t.label === "How To" ? "#D6BE7A" : C.amberHi, fontWeight: 600, minWidth: 70, fontVariationSettings: '"opsz" 24, "SOFT" 70' }}>{t.label}</div>
            <div style={{ fontFamily: "var(--font-display)", fontSize: 13, color: C.muted, fontVariationSettings: '"opsz" 24, "SOFT" 50' }}>{t.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ⌘K command palette — a stylized version of the universal search
// modal. Shows the keyboard shortcut, a query in the input, and a
// few mixed result rows (a person, a how-to topic, a quick action)
// so the user understands what *can* land here.
function CmdKArt(): JSX.Element {
  const results = [
    { kind: "PERSON", title: "Smith, John", sub: "DOB 1985-04-12 · 7 records · Havoc 67", color: C.amberHi },
    { kind: "TOPIC",  title: "Recall a bench warrant",   sub: "How To · 4 min", color: "#9DA8FF" },
    { kind: "TOPIC",  title: "Order a NJ birth certificate", sub: "How To · 6 min", color: "#9DA8FF" },
    { kind: "ACTION", title: "Open Stats",               sub: "Jump to /stats", color: "#7BD495" },
  ];
  return (
    <div style={{ ...ART_CARD, padding: "16px 18px" }}>
      <div style={ART_LABEL}>Universal Command Palette</div>
      {/* Search input mockup */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "11px 14px",
          borderRadius: 10,
          background: "rgba(8,12,20,0.55)",
          border: `1px solid ${C.lineHi}`,
          marginTop: 6,
          marginBottom: 10,
          animation: "tour-art-pulse 3.2s ease-in-out infinite",
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.18em",
            padding: "2px 7px",
            borderRadius: 5,
            background: "rgba(77, 168, 255, 0.18)",
            color: "#7CC2FF",
            border: "1px solid rgba(77, 168, 255, 0.40)",
          }}
        >
          ⌘K
        </div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 13, color: C.text, flex: 1 }}>
          john<span style={{ borderLeft: "1.5px solid #7CC2FF", marginLeft: 1, animation: "tour-art-pulse 1.1s steps(2) infinite" }} />
        </div>
        <div style={{ fontFamily: "var(--font-body)", fontSize: 10, color: C.dim, letterSpacing: "0.08em" }}>
          ESC to close
        </div>
      </div>
      {/* Result rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {results.map((r, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 11,
              padding: "8px 12px",
              borderRadius: 8,
              background: i === 0 ? "rgba(232,160,80,0.10)" : "transparent",
              border: i === 0 ? "1px solid rgba(232,160,80,0.28)" : "1px solid transparent",
              animation: `tour-art-slide 500ms cubic-bezier(0.2, 0.8, 0.2, 1) ${i * 80}ms both`,
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 9,
                letterSpacing: "0.15em",
                fontWeight: 700,
                color: r.color,
                minWidth: 56,
              }}
            >
              {r.kind}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: C.text, fontWeight: 600 }}>
                {r.title}
              </div>
              <div style={{ fontFamily: "var(--font-body)", fontSize: 10, color: C.dim, marginTop: 2 }}>
                {r.sub}
              </div>
            </div>
            {i === 0 && (
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: C.dim }}>↵</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Ask Jack AI orb — orb + a mock chat bubble showing the kind of
// answer it returns (with a citation chip). Reinforces the value
// prop: trained on the LSM corpus, refuses off-topic, cites sources.
function AiOrbArt(): JSX.Element {
  return (
    <div style={{ ...ART_CARD, padding: "18px 18px" }}>
      <div style={ART_LABEL}>Ask Jack · NJRC AI Assistant</div>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginTop: 8 }}>
        {/* Mini orb — same construction as the nav orb but bigger */}
        <div
          style={{
            position: "relative",
            width: 72,
            height: 72,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: -12,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(77,168,255,0.30) 0%, rgba(77,168,255,0) 70%)",
              animation: "tour-art-glow 2.2s ease-in-out infinite",
            }}
          />
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: -4,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(124,194,255,0.45) 0%, rgba(124,194,255,0) 70%)",
              animation: "tour-art-glow 1.6s ease-in-out infinite reverse",
            }}
          />
          <div
            style={{
              position: "relative",
              width: 56,
              height: 56,
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 32% 30%, #BFE2FF 0%, #4DA8FF 55%, #1D4ED8 100%)",
              boxShadow:
                "0 0 22px rgba(77,168,255,0.55), inset 0 -6px 12px rgba(0,0,0,0.25), inset 0 4px 10px rgba(255,255,255,0.40)",
            }}
          />
        </div>
        {/* Chat bubbles */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
          {/* User question */}
          <div
            style={{
              alignSelf: "flex-end",
              maxWidth: "85%",
              padding: "8px 12px",
              borderRadius: "12px 12px 3px 12px",
              background: "rgba(232,160,80,0.16)",
              border: "1px solid rgba(232,160,80,0.32)",
              fontFamily: "var(--font-body)",
              fontSize: 12,
              color: C.text,
              lineHeight: 1.4,
              animation: "tour-art-slide 500ms cubic-bezier(0.2, 0.8, 0.2, 1) 0ms both",
            }}
          >
            How do I file a CN-11208 fee waiver?
          </div>
          {/* Jack reply */}
          <div
            style={{
              alignSelf: "flex-start",
              maxWidth: "92%",
              padding: "10px 12px",
              borderRadius: "12px 12px 12px 3px",
              background: "rgba(77, 168, 255, 0.10)",
              border: "1px solid rgba(77, 168, 255, 0.30)",
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontVariationSettings: '"opsz" 24, "SOFT" 50',
              fontSize: 12,
              color: C.muted,
              lineHeight: 1.45,
              animation: "tour-art-slide 500ms cubic-bezier(0.2, 0.8, 0.2, 1) 220ms both",
            }}
          >
            Eligibility is ≤150% Federal Poverty Level + ≤$2,500 spendable assets. Pre-fill from the petitioner details modal — the form auto-fills inside the expungement packet.
          </div>
          {/* Citation chip */}
          <div
            style={{
              alignSelf: "flex-start",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "3px 9px",
              borderRadius: 999,
              background: "rgba(77,168,255,0.06)",
              border: "1px solid rgba(77,168,255,0.32)",
              fontFamily: "var(--font-body)",
              fontSize: 10,
              color: "#7CC2FF",
              fontWeight: 600,
              animation: "tour-art-slide 500ms cubic-bezier(0.2, 0.8, 0.2, 1) 440ms both",
            }}
          >
            <span style={{ fontSize: 8 }}>📖</span>
            LSM 2026 · Module 14 · Fee Waivers
          </div>
        </div>
      </div>
    </div>
  );
}

// Reusable styles for the slide-art "card" shell. All illustrations
// sit inside this rounded glass-blur container so they share visual
// gravity across the slideshow. Matched to the live app's bg-card +
// warm-line palette so they feel like screenshots of the real product.
const ART_CARD: CSSProperties = {
  width: "min(560px, 92vw)",
  minHeight: 220,
  padding: "20px 22px",
  borderRadius: 14,
  background: C.glass,
  backdropFilter: "blur(20px) saturate(1.2)",
  WebkitBackdropFilter: "blur(20px) saturate(1.2)",
  border: `1px solid ${C.lineHi}`,
  boxShadow: "0 22px 60px rgba(0,0,0,0.50), 0 0 0 1px rgba(242,237,223,0.04), inset 0 1px 0 rgba(255,255,255,0.04)",
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const ART_LABEL: CSSProperties = {
  fontFamily: "var(--font-display)",
  fontSize: 10,
  letterSpacing: 2,
  textTransform: "uppercase",
  color: C.amberHi,
  fontWeight: 600,
  fontStyle: "italic",
  fontVariationSettings: '"opsz" 24, "SOFT" 70',
};

function TodayBriefArt(): JSX.Element {
  return (
    <div style={ART_CARD}>
      <div style={ART_LABEL}>Today's Brief · Friday, May 9</div>
      <div style={{ fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: 18, color: C.text, lineHeight: 1.4, animation: "tour-art-pulse 4s ease-in-out infinite" }}>
        "Good morning, Damon. It's Friday — three intakes overnight, two warrants need a follow-up call, and Robinson's hearing is at 2:30."
      </div>
      <div style={{ height: 1, background: "rgba(242,237,223,0.12)", margin: "8px 0" }} />
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {["3 intakes today", "2 warrants", "1 hearing", "5 follow-ups"].map((t, i) => (
          <div key={i} style={{ padding: "6px 10px", borderRadius: 999, background: "rgba(196,122,50,0.18)", border: "1px solid rgba(232,160,80,0.40)", color: C.text, fontSize: 11, fontFamily: "var(--font-body)", fontWeight: 600 }}>
            {t}
          </div>
        ))}
      </div>
    </div>
  );
}

function LookupFormArt(): JSX.Element {
  return (
    <div style={ART_CARD}>
      <div style={ART_LABEL}>Add a Participant</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 4 }}>
        <FakeInput label="First name" value="John" />
        <FakeInput label="Last name" value="Smith" />
        <FakeInput label="Date of birth" value="1985-04-12" />
        <FakeInput label="Notes (optional)" value="" />
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
        <div style={{ padding: "9px 22px", borderRadius: 8, background: `linear-gradient(180deg, ${C.sage}, ${C.sageDeep})`, color: "#0a1310", fontSize: 12, fontWeight: 700, fontFamily: "var(--font-body)", letterSpacing: 0.5, animation: "tour-art-pulse 2.6s ease-in-out infinite" }}>
          Submit →
        </div>
      </div>
    </div>
  );
}

function FakeInput({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <div style={{ fontSize: 9, letterSpacing: 1.4, textTransform: "uppercase", color: C.dim, fontWeight: 700, marginBottom: 4 }}>{label}</div>
      <div style={{ height: 32, padding: "8px 10px", borderRadius: 6, background: "rgba(8,12,20,0.55)", border: "1px solid rgba(242,237,223,0.16)", color: value ? C.text : C.dim, fontFamily: "var(--font-mono)", fontSize: 12 }}>
        {value || "—"}
      </div>
    </div>
  );
}

// People list — card grid mockup matching the live People page. Tiles
// are HSL-interpolated by havoc score (green at 0 → bordeaux at 100)
// and dotted with faint "crime icon" glyph watermarks so the user
// sees the at-a-glance pattern the live page uses.
function PeopleListArt(): JSX.Element {
  // Three tiles spanning the score range so the gradient is visible.
  const tiles = [
    { name: "James, Tyrone",   tier: "STEADY",   havoc: 12, records: 2, glyphs: ["⚖"] },
    { name: "Robinson, Marlon", tier: "ROUGH",    havoc: 54, records: 7, glyphs: ["🚗", "💊"] },
    { name: "Smith, John",     tier: "CHAOS",    havoc: 89, records: 14, glyphs: ["🚗", "💊", "⚔", "🎯"] },
  ];
  // Score-to-color matches RecordsPage's tierBgGradient: 140° green →
  // 50° gold → 355° bordeaux.
  const scoreColor = (score: number) => {
    const t = Math.max(0, Math.min(100, score)) / 100;
    let h: number;
    if (t < 0.5) h = 140 - (t / 0.5) * 90;
    else { h = 50 - ((t - 0.5) / 0.5) * 55; if (h < 0) h += 360; }
    return `linear-gradient(155deg, hsl(${h}, 52%, 32%) 0%, hsl(${h}, 56%, 18%) 100%)`;
  };
  return (
    <div style={{ ...ART_CARD, padding: "16px 16px" }}>
      <div style={ART_LABEL}>People · color-coded by Havoc</div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 8,
          marginTop: 6,
        }}
      >
        {tiles.map((t, i) => (
          <div
            key={t.name}
            style={{
              position: "relative",
              overflow: "hidden",
              padding: "12px 12px",
              borderRadius: 10,
              background: scoreColor(t.havoc),
              border: "1px solid rgba(255,255,255,0.10)",
              minHeight: 124,
              display: "flex",
              flexDirection: "column",
              animation: `tour-art-slide 600ms cubic-bezier(0.2, 0.8, 0.2, 1) ${i * 110}ms both`,
            }}
          >
            {/* Crime-icon watermark scatter */}
            <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
              {t.glyphs.map((g, gi) => (
                <span
                  key={gi}
                  style={{
                    position: "absolute",
                    fontSize: 32,
                    color: "white",
                    opacity: 0.09,
                    top: `${15 + (gi % 2) * 50}%`,
                    left: `${10 + ((gi * 37) % 70)}%`,
                    transform: `rotate(${[-8, 6, -14, 10][gi % 4]}deg)`,
                  }}
                >
                  {g}
                </span>
              ))}
            </div>
            {/* Top: tier eyebrow */}
            <div
              style={{
                fontFamily: "var(--font-body)",
                fontSize: 8,
                fontWeight: 700,
                letterSpacing: "0.22em",
                color: "rgba(255,255,255,0.72)",
                position: "relative",
              }}
            >
              {t.tier}
            </div>
            {/* Name */}
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 13,
                fontWeight: 700,
                lineHeight: 1.1,
                color: "white",
                marginTop: 4,
                fontVariationSettings: '"opsz" 60, "SOFT" 50',
                position: "relative",
              }}
            >
              {t.name}
            </div>
            {/* Bottom: havoc score + records, mini */}
            <div
              style={{
                marginTop: "auto",
                paddingTop: 10,
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
                position: "relative",
                borderTop: "1px solid rgba(255,255,255,0.18)",
              }}
            >
              <div>
                <div style={{ fontFamily: "var(--font-body)", fontSize: 7, fontWeight: 700, letterSpacing: "0.22em", color: "rgba(255,255,255,0.7)" }}>HAVOC</div>
                <div style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 700, color: "white", lineHeight: 1, fontVariationSettings: '"opsz" 144, "SOFT" 30' }}>{t.havoc}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: "var(--font-body)", fontSize: 7, fontWeight: 700, letterSpacing: "0.22em", color: "rgba(255,255,255,0.7)" }}>RECORDS</div>
                <div style={{ fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 700, color: "white", lineHeight: 1, fontVariationSettings: '"opsz" 144, "SOFT" 30' }}>{t.records}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Person hero — bordeaux full-bleed card matching the live PersonPage.
// Eyebrow + AI nickname + giant Fraunces name on the left; havoc score
// + tier pill in the upper-right; thumbtack pin button beside it.
function PersonHeroArt(): JSX.Element {
  return (
    <div style={{ ...ART_CARD, padding: 0, overflow: "hidden" }}>
      {/* Bordeaux full-bleed hero */}
      <div
        style={{
          position: "relative",
          background: "linear-gradient(135deg, #8C1F2F 0%, #6B1825 100%)",
          padding: "16px 18px 22px 18px",
          color: "#F2EDDF",
        }}
      >
        {/* Thumbtack pin button — upper right corner */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            width: 26,
            height: 26,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.12)",
            border: "1px solid rgba(255,255,255,0.22)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            color: "#D6BE7A",
          }}
        >
          📌
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 16 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 9,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.7)",
                marginBottom: 4,
              }}
            >
              Captured Person · 7 records
            </div>
            {/* AI-written nickname (italic, gold) */}
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontStyle: "italic",
                fontSize: 12,
                color: "#D6BE7A",
                marginBottom: 6,
                fontVariationSettings: '"opsz" 24, "SOFT" 60',
              }}
            >
              "the Newark regular"
            </div>
            {/* Giant Fraunces name */}
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 32,
                color: "#F2EDDF",
                lineHeight: 0.95,
                fontWeight: 600,
                letterSpacing: "-0.025em",
                fontVariationSettings: '"opsz" 144, "SOFT" 50',
              }}
            >
              John Smith
            </div>
            {/* AI bio one-liner */}
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontStyle: "italic",
                fontSize: 11,
                color: "rgba(255,255,255,0.8)",
                marginTop: 8,
                lineHeight: 1.4,
                fontVariationSettings: '"opsz" 24, "SOFT" 50',
              }}
            >
              Clean since '23. One outstanding fine blocking his license restoration.
            </div>
          </div>
          {/* Havoc score corner — number + tier pill */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0 }}>
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 8,
                letterSpacing: "0.22em",
                color: "rgba(255,255,255,0.65)",
                marginBottom: 2,
              }}
            >
              HAVOC
            </div>
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 48,
                fontWeight: 700,
                color: "#F2EDDF",
                lineHeight: 1,
                fontVariationSettings: '"opsz" 144, "SOFT" 30',
                animation: "tour-art-pulse 3.4s ease-in-out infinite",
              }}
            >
              67
            </div>
            <div
              style={{
                marginTop: 6,
                padding: "3px 10px",
                borderRadius: 999,
                background: "linear-gradient(135deg, #c47a32, #E0584A)",
                color: "white",
                fontFamily: "var(--font-body)",
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
              }}
            >
              Rough
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PersonRecordsArt(): JSX.Element {
  const rows = [
    { yr: "2024", court: "Newark Muni",   chg: "Disorderly",  status: "Disposed",  color: "#9CC8FF" },
    { yr: "2022", court: "MVC",           chg: "Suspension",  status: "Active",    color: "#FF8A80" },
    { yr: "2019", court: "Essex Sup",     chg: "Possession",  status: "Expunged",  color: "#A0E0A8" },
  ];
  return (
    <div style={ART_CARD}>
      <div style={ART_LABEL}>Records · 3 of 12</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
        {rows.map((r, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "44px 1fr 100px 80px", gap: 10, alignItems: "center", padding: "10px 12px", borderRadius: 8, background: "rgba(8,12,20,0.45)", border: "1px solid rgba(242,237,223,0.06)", animation: `tour-art-slide 600ms cubic-bezier(0.2, 0.8, 0.2, 1) ${i * 100}ms both` }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: C.dim }}>{r.yr}</div>
            <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: C.text, fontWeight: 500 }}>{r.chg} <span style={{ color: C.dim, fontSize: 10 }}>· {r.court}</span></div>
            <div style={{ padding: "3px 8px", borderRadius: 999, background: `${r.color}22`, color: r.color, fontSize: 10, fontWeight: 600, fontFamily: "var(--font-body)", textAlign: "center" }}>{r.status}</div>
            <div style={{ color: C.dim, fontSize: 11, textAlign: "right" }}>→</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Actions menu — orange ACTIONS chip at top, then three labeled groups
// of leaf items below. Each group's items pop a tiny tinted icon so
// the eye can see the categorical structure at a glance, and the
// menu reads as the "factory floor" of NJRC outputs.
function ActionsMenuArt(): JSX.Element {
  // Two items per group keeps the whole illustration under ~360px
  // tall — the slideshow card slot has ~380-440px depending on
  // viewport, so trimming to 6 leaves headroom and avoids clipping.
  const groups = [
    {
      label: "For the participant",
      tone: "#7BD495", // emerald
      items: [
        { icon: "🎙", title: "Start a meeting",   sub: "Live AI transcription" },
        { icon: "📄", title: "Resume + handouts", sub: "Drafted from their record" },
      ],
    },
    {
      label: "Court documents",
      tone: "#9DA8FF", // periwinkle
      items: [
        { icon: "⚖",  title: "Expungement packet",  sub: "Petition + waiver + cover" },
        { icon: "🛂", title: "MVC restoration",     sub: "DO-21, surcharge plan, letters" },
      ],
    },
    {
      label: "Internal exports",
      tone: "#E2A658", // copper
      items: [
        { icon: "📦", title: "Full record ZIP",     sub: "Every doc, every scrape" },
        { icon: "🖨", title: "Print summary",       sub: "Single-page intake recap" },
      ],
    },
  ];
  return (
    <div style={{ ...ART_CARD, padding: "16px 16px" }}>
      <div style={ART_LABEL}>Actions menu · 1 button, every output</div>
      {/* The orange ACTIONS chip sits at the top, just like the
          floating dock anchor on the live page. */}
      <div style={{ display: "flex", justifyContent: "center", marginTop: 4, marginBottom: 8 }}>
        <div
          style={{
            position: "relative",
            padding: "8px 24px",
            borderRadius: 999,
            background: "linear-gradient(135deg, #FF8A65, #E0584A)",
            color: "#fff",
            fontFamily: "var(--font-body)",
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            boxShadow: "0 8px 22px rgba(224,88,74,0.45), inset 0 1px 0 rgba(255,255,255,0.30)",
            animation: "tour-art-pulse 2.4s ease-in-out infinite",
          }}
        >
          Actions ▾
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 4 }}>
        {groups.map((g, gi) => (
          <div
            key={g.label}
            style={{
              animation: `tour-art-slide 500ms cubic-bezier(0.2, 0.8, 0.2, 1) ${gi * 130}ms both`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 9,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: g.tone,
                fontWeight: 700,
                marginBottom: 5,
              }}
            >
              <span style={{ width: 5, height: 5, borderRadius: "50%", background: g.tone }} />
              {g.label}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {g.items.map((it) => (
                <div
                  key={it.title}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 9,
                    padding: "6px 9px",
                    borderRadius: 7,
                    background: "rgba(8,12,20,0.55)",
                    border: `1px solid ${g.tone}33`,
                  }}
                >
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 6,
                      background: `${g.tone}1a`,
                      border: `1px solid ${g.tone}44`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      flexShrink: 0,
                    }}
                  >
                    {it.icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "var(--font-body)", fontSize: 11, color: C.text, fontWeight: 600, lineHeight: 1.2 }}>
                      {it.title}
                    </div>
                    <div style={{ fontFamily: "var(--font-body)", fontSize: 9, color: C.dim, marginTop: 1 }}>
                      {it.sub}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Stats — mirrors the live page: a chapter "frontispiece" card (colored
// stripe + section number + giant italic title) followed by a snippet
// of LLM commentary ("The Read") and a tidy bar chart preview. Keeps
// the visual identity of the actual Stats page intact.
function StatsArt(): JSX.Element {
  // Periwinkle for "section 03" — matches the live SECTION_HEADING_COLORS rotation.
  const sectionColor = "#9DA8FF";
  const bars = [40, 75, 55, 90, 60, 80, 45, 95, 70, 85];
  return (
    <div style={{ ...ART_CARD, padding: "16px 16px" }}>
      <div style={ART_LABEL}>Stats · chapter sections + LLM commentary</div>
      {/* Chapter frontispiece — left stripe + eyebrow + giant italic */}
      <div
        style={{
          position: "relative",
          marginTop: 4,
          padding: "12px 16px 14px 22px",
          borderRadius: 12,
          background: `linear-gradient(135deg, ${sectionColor}26 0%, ${sectionColor}0d 100%)`,
          border: `1.5px solid ${sectionColor}60`,
          overflow: "hidden",
          animation: "tour-art-slide 500ms cubic-bezier(0.2, 0.8, 0.2, 1) both",
        }}
      >
        {/* Left stripe */}
        <span
          aria-hidden
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            width: 5,
            background: `linear-gradient(180deg, ${sectionColor}, ${sectionColor}80)`,
          }}
        />
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 9,
            letterSpacing: "0.28em",
            textTransform: "uppercase",
            color: sectionColor,
            fontWeight: 700,
            marginBottom: 4,
          }}
        >
          Section 03
        </div>
        <div
          style={{
            fontFamily: "var(--font-display)",
            fontStyle: "italic",
            fontSize: 22,
            fontWeight: 500,
            lineHeight: 1.0,
            letterSpacing: "-0.025em",
            color: `color-mix(in oklab, ${sectionColor} 70%, white 30%)`,
            fontVariationSettings: '"opsz" 144, "SOFT" 40',
          }}
        >
          When and how often
        </div>
      </div>
      {/* "The Read" — LLM commentary mockup */}
      <div
        style={{
          marginTop: 10,
          padding: "10px 14px",
          borderRadius: 10,
          background: "rgba(8,12,20,0.55)",
          border: "1px solid rgba(232,160,80,0.28)",
          animation: "tour-art-slide 500ms cubic-bezier(0.2, 0.8, 0.2, 1) 200ms both",
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 8,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: "#D6BE7A",
            fontWeight: 700,
            marginBottom: 4,
          }}
        >
          The Read
        </div>
        <div
          style={{
            fontFamily: "var(--font-display)",
            fontStyle: "italic",
            fontSize: 11,
            color: C.muted,
            lineHeight: 1.5,
            fontVariationSettings: '"opsz" 24, "SOFT" 50',
          }}
        >
          Volume peaked in <strong style={{ color: C.text, fontStyle: "normal", fontWeight: 600 }}>2019</strong> with 412 records — driven mostly by Newark Muni traffic stops. The post-2020 dip didn't come back: caseload now sits 38% below peak, but severity skews higher.
        </div>
      </div>
      {/* Compact chart preview */}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 5, height: 56, marginTop: 10 }}>
        {bars.map((h, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: `${h}%`,
              background: `linear-gradient(180deg, ${sectionColor} 0%, ${sectionColor}40 100%)`,
              borderRadius: "3px 3px 1px 1px",
              animation: `tour-art-grow 700ms cubic-bezier(0.2, 0.8, 0.2, 1) ${300 + i * 50}ms both`,
              transformOrigin: "bottom",
              opacity: 0.85,
            }}
          />
        ))}
      </div>
    </div>
  );
}

// How To — colored section headlines (each section has its own hue
// matching the live page) with a few topic cards under each, plus a
// reminder that Ask Jack is the chat-style entry into the same corpus.
function HowToArt(): JSX.Element {
  const sections = [
    {
      color: "#7CC2FF", // sky blue
      title: "Getting started",
      icon: "📘",
      topics: ["What does an LSC do?", "The NJRC program flow"],
    },
    {
      color: "#7BD495", // emerald
      title: "Birth certificates",
      icon: "🪪",
      topics: ["50 states · picker", "Another country · picker"],
    },
    {
      color: "#E2A658", // copper
      title: "MVC restoration",
      icon: "🚗",
      topics: ["DO-21 timeline", "Surcharge balance lookup"],
    },
  ];
  return (
    <div style={{ ...ART_CARD, padding: "14px 16px" }}>
      <div style={ART_LABEL}>How To · the LSM 2026 handbook</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 4 }}>
        {sections.map((s, si) => (
          <div
            key={s.title}
            style={{
              animation: `tour-art-slide 500ms cubic-bezier(0.2, 0.8, 0.2, 1) ${si * 110}ms both`,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 6,
                  background: `${s.color}1f`,
                  border: `1px solid ${s.color}66`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  flexShrink: 0,
                }}
              >
                {s.icon}
              </div>
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontStyle: "italic",
                  fontSize: 16,
                  fontWeight: 600,
                  color: `color-mix(in oklab, ${s.color} 70%, white 30%)`,
                  lineHeight: 1,
                  fontVariationSettings: '"opsz" 36, "SOFT" 50',
                }}
              >
                {s.title}
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", paddingLeft: 30 }}>
              {s.topics.map((t) => (
                <div
                  key={t}
                  style={{
                    padding: "4px 9px",
                    borderRadius: 6,
                    background: "rgba(8,12,20,0.55)",
                    border: `1px solid ${s.color}33`,
                    color: C.text,
                    fontSize: 10,
                    fontFamily: "var(--font-body)",
                  }}
                >
                  {t}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {/* Ask Jack reminder pill */}
      <div
        style={{
          marginTop: 10,
          padding: "7px 12px",
          borderRadius: 999,
          background: "rgba(77, 168, 255, 0.10)",
          border: "1px solid rgba(77, 168, 255, 0.32)",
          color: "#7CC2FF",
          fontFamily: "var(--font-display)",
          fontStyle: "italic",
          fontSize: 11,
          alignSelf: "flex-start",
          fontVariationSettings: '"opsz" 24, "SOFT" 50',
        }}
      >
        Or just <span style={{ fontStyle: "normal", fontWeight: 700, color: "#BFE2FF" }}>Ask Jack</span> — same corpus, conversational
      </div>
    </div>
  );
}

function GenericArt(): JSX.Element {
  return (
    <div style={{ ...ART_CARD, alignItems: "center", justifyContent: "center", minHeight: 180 }}>
      <div style={{ fontFamily: "var(--font-display)", fontStyle: "italic", color: C.muted }}>(illustration)</div>
    </div>
  );
}

// Particle-system intro orb. Canvas-rendered 60fps. Three concentric
// rings of particles orbiting at different speeds + a glowing core +
// breathing outer halo. Designed to feel alive rather than decorative.
export type OrbState = "idle" | "thinking" | "speaking";

interface OrbPalette {
  particle:    [number, number, number];
  particleHi:  [number, number, number];
  halo:        [number, number, number];
  rim:         [number, number, number];
  coreInner:   [number, number, number];
  coreOuter:   [number, number, number];
  contour:     [number, number, number];
}
// Three color personalities for Jack's orb. Idle = brand sky-blue
// (the original cosmic look). Thinking = violet/neural (the mind
// is working). Speaking = warm gold (output flowing out). Colors
// ease between these three palettes per-frame so transitions feel
// alive rather than snapping.
const ORB_PALETTES: Record<OrbState, OrbPalette> = {
  idle: {
    particle:   [77, 168, 255],
    particleHi: [124, 194, 255],
    halo:       [77, 168, 255],
    rim:        [77, 168, 255],
    coreInner:  [124, 194, 255],
    coreOuter:  [37, 99, 235],
    contour:    [77, 168, 255],
  },
  thinking: {
    particle:   [162, 107, 255],
    particleHi: [200, 152, 255],
    halo:       [162, 107, 255],
    rim:        [180, 120, 255],
    coreInner:  [210, 165, 255],
    coreOuter:  [110, 60, 200],
    contour:    [162, 107, 255],
  },
  speaking: {
    particle:   [255, 184, 80],
    particleHi: [255, 215, 130],
    halo:       [255, 184, 80],
    rim:        [255, 200, 100],
    coreInner:  [255, 220, 150],
    coreOuter:  [220, 130, 40],
    contour:    [255, 184, 80],
  },
};
function lerpRgb(a: [number, number, number], b: [number, number, number], t: number): [number, number, number] {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}
function rgba(c: [number, number, number], alpha: number): string {
  return `rgba(${c[0].toFixed(0)},${c[1].toFixed(0)},${c[2].toFixed(0)},${alpha})`;
}

export function BigIntroOrb({
  size,
  active = false,
  state,
}: {
  size: number;
  active?: boolean;
  /** Three-state mode: idle (default cosmic), thinking (violet), speaking (gold).
   *  Backward-compat: when omitted, the legacy `active` boolean maps to
   *  thinking=true / idle=false. */
  state?: OrbState;
}): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Resolve state from either prop. The new `state` prop wins; older
  // callers passing only `active` still get a sensible thinking-or-idle.
  const resolvedState: OrbState = state ?? (active ? "thinking" : "idle");
  const stateRef = useRef<OrbState>(resolvedState);
  stateRef.current = resolvedState;
  // Legacy hook — keep activeRef around since "active" still drives
  // orbital speed / halo amplitude / rim pulse (intensity, not color).
  // Both "thinking" and "speaking" count as active for that purpose.
  const activeRef = useRef(active || resolvedState !== "idle");
  activeRef.current = active || resolvedState !== "idle";
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    // Generous canvas — needs room for halo + outer particle ring
    const canvasSize = size * 2.4;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = canvasSize * dpr;
    canvas.height = canvasSize * dpr;
    canvas.style.width = `${canvasSize}px`;
    canvas.style.height = `${canvasSize}px`;
    ctx.scale(dpr, dpr);

    const cx = canvasSize / 2;
    const cy = canvasSize / 2;
    const coreR = size * 0.42;

    // Three particle bands — inner orbit (close, fast), mid (medium),
    // outer (slow, dispersed). Each particle has its own spec.
    interface OrbParticle {
      angle: number;
      radius: number;
      angularVel: number;
      bright: number;
      sizePx: number;
      bandJitter: number;
    }
    const inner: OrbParticle[] = Array.from({ length: 22 }).map(() => ({
      angle: Math.random() * Math.PI * 2,
      radius: coreR * 1.18 + Math.random() * coreR * 0.15,
      angularVel: 0.0024 + Math.random() * 0.002,
      bright: 0.55 + Math.random() * 0.4,
      sizePx: 1.2 + Math.random() * 1.6,
      bandJitter: Math.random() * Math.PI * 2,
    }));
    const mid: OrbParticle[] = Array.from({ length: 32 }).map(() => ({
      angle: Math.random() * Math.PI * 2,
      radius: coreR * 1.55 + Math.random() * coreR * 0.25,
      angularVel: -(0.0014 + Math.random() * 0.0014),
      bright: 0.35 + Math.random() * 0.4,
      sizePx: 0.9 + Math.random() * 1.4,
      bandJitter: Math.random() * Math.PI * 2,
    }));
    const outer: OrbParticle[] = Array.from({ length: 48 }).map(() => ({
      angle: Math.random() * Math.PI * 2,
      radius: coreR * 2.0 + Math.random() * coreR * 0.6,
      angularVel: 0.0008 + Math.random() * 0.001,
      bright: 0.18 + Math.random() * 0.3,
      sizePx: 0.7 + Math.random() * 1.0,
      bandJitter: Math.random() * Math.PI * 2,
    }));

    let raf = 0;
    // Smoothed activity factor — eases between idle (0) and full
    // thinking/speaking (1) so the transition isn't a sudden snap
    // when the chat starts/finishes a response. Controls intensity
    // (speed, glow, breath) but NOT color — color is driven by the
    // separate palette ease below.
    let activity = 0;
    // Per-channel current palette — eases toward the target palette
    // for the current state. Cloned so we mutate without affecting
    // the canonical ORB_PALETTES.
    const current: OrbPalette = {
      particle:   [...ORB_PALETTES.idle.particle]   as [number, number, number],
      particleHi: [...ORB_PALETTES.idle.particleHi] as [number, number, number],
      halo:       [...ORB_PALETTES.idle.halo]       as [number, number, number],
      rim:        [...ORB_PALETTES.idle.rim]        as [number, number, number],
      coreInner:  [...ORB_PALETTES.idle.coreInner]  as [number, number, number],
      coreOuter:  [...ORB_PALETTES.idle.coreOuter]  as [number, number, number],
      contour:    [...ORB_PALETTES.idle.contour]    as [number, number, number],
    };
    // Ease factor per frame — 0.04 gives a ~25-frame (~400ms at 60fps)
    // transition between palettes. Fast enough to feel responsive,
    // slow enough that the eye can see the color shift.
    const COLOR_EASE = 0.04;
    const draw = (t: number) => {
      const time = t / 1000;
      // Ease activity toward target over ~600ms.
      const target = activeRef.current ? 1 : 0;
      activity += (target - activity) * 0.05;
      // Active multipliers — orbital speed, halo, breath, rim, pulse.
      const speedMul = 1 + activity * 1.6; // up to 2.6× faster
      const haloAmp = 1 + activity * 1.0;
      const rimAmp = 1 + activity * 1.4;

      // Ease palette colors toward the target state's palette.
      const targetPal = ORB_PALETTES[stateRef.current];
      current.particle   = lerpRgb(current.particle,   targetPal.particle,   COLOR_EASE);
      current.particleHi = lerpRgb(current.particleHi, targetPal.particleHi, COLOR_EASE);
      current.halo       = lerpRgb(current.halo,       targetPal.halo,       COLOR_EASE);
      current.rim        = lerpRgb(current.rim,        targetPal.rim,        COLOR_EASE);
      current.coreInner  = lerpRgb(current.coreInner,  targetPal.coreInner,  COLOR_EASE);
      current.coreOuter  = lerpRgb(current.coreOuter,  targetPal.coreOuter,  COLOR_EASE);
      current.contour    = lerpRgb(current.contour,    targetPal.contour,    COLOR_EASE);

      ctx.clearRect(0, 0, canvasSize, canvasSize);
      ctx.globalCompositeOperation = "lighter";

      // Layer 1: outer halo — pulsing in the current accent color.
      const haloFreq = 0.9 + activity * 1.6;
      const haloPulse = (0.85 + 0.15 * Math.sin(time * haloFreq)) * haloAmp;
      const halo = ctx.createRadialGradient(cx, cy, coreR * 0.6, cx, cy, coreR * 2.8);
      halo.addColorStop(0,   rgba(current.halo, 0.22 * haloPulse));
      halo.addColorStop(0.4, rgba(current.halo, 0.10 * haloPulse));
      halo.addColorStop(1,   rgba(current.halo, 0));
      ctx.fillStyle = halo;
      ctx.fillRect(0, 0, canvasSize, canvasSize);

      // Layer 2: orbital particles — speed multiplied by activity.
      for (const ring of [outer, mid, inner]) {
        for (const p of ring) {
          p.angle += p.angularVel * speedMul;
          // Tiny radial breath so the band is alive
          const r = p.radius + Math.sin(time * 1.4 + p.bandJitter) * (1.2 + activity * 1.8);
          const x = cx + Math.cos(p.angle) * r;
          const y = cy + Math.sin(p.angle) * r;
          // Twinkle: brightness modulates with a per-particle phase
          const twinkle = 0.6 + 0.4 * Math.sin(time * (2.2 + activity * 1.6) + p.bandJitter * 3);
          const alpha = p.bright * twinkle * (1 + activity * 0.4);
          const grad = ctx.createRadialGradient(x, y, 0, x, y, p.sizePx * 4);
          grad.addColorStop(0,   rgba(current.particleHi, alpha));
          grad.addColorStop(0.4, rgba(current.particle,   alpha * 0.6));
          grad.addColorStop(1,   rgba(current.particle,   0));
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.arc(x, y, p.sizePx * 4, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // Layer 3: core rim glow (additive bloom). When active, glow
      // pulses on its own faster cycle for an "energetic" feel.
      const rimPulse = activity > 0.05
        ? 0.7 + 0.3 * Math.sin(time * 3.2)
        : 1;
      const rim = ctx.createRadialGradient(cx, cy, coreR * 0.7, cx, cy, coreR * 1.8);
      rim.addColorStop(0, rgba(current.rim, 0.4 * rimAmp * rimPulse));
      rim.addColorStop(1, rgba(current.rim, 0));
      ctx.fillStyle = rim;
      ctx.fillRect(0, 0, canvasSize, canvasSize);

      ctx.globalCompositeOperation = "source-over";

      // Layer 4: solid core sphere with specular highlight. Breath
      // amplitude grows with activity.
      const breath = 0.97 + (0.03 + activity * 0.05) * Math.sin(time * (1.4 + activity * 1.2));
      const r = coreR * breath;
      const hx = cx - r * 0.32;
      const hy = cy - r * 0.36;
      const coreGrad = ctx.createRadialGradient(hx, hy, r * 0.05, cx, cy, r);
      coreGrad.addColorStop(0,    "rgba(255,255,255,0.96)");
      coreGrad.addColorStop(0.18, rgba(current.coreInner, 0.95));
      coreGrad.addColorStop(0.7,  rgba(current.halo, 0.65));
      coreGrad.addColorStop(1,    rgba(current.coreOuter, 0.12));
      ctx.fillStyle = coreGrad;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();

      // Specular highlight (always white — looks like sunlight on the
      // sphere regardless of body color)
      const spec = ctx.createRadialGradient(hx, hy, 0, hx, hy, r * 0.55);
      spec.addColorStop(0, "rgba(255,255,255,0.65)");
      spec.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = spec;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();

      // Crisp contour in the current accent color
      ctx.strokeStyle = rgba(current.contour, 0.55);
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.stroke();

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [size]);
  const canvasSize = size * 2.4;
  return (
    <canvas
      ref={canvasRef}
      style={{
        width: canvasSize,
        height: canvasSize,
        display: "block",
      }}
      aria-hidden="true"
    />
  );
}

// ─── Progress Ribbon ───────────────────────────────────────────────────
// Thin sage line at the top of the viewport that fills as the tour
// progresses. Always visible during the tour, fades when dismissed.

function ProgressRibbon({ idx, total }: { idx: number; total: number }): JSX.Element {
  const pct = (idx / Math.max(1, total - 1)) * 100;
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: "rgba(255,255,255,0.06)",
        zIndex: 9999,
        pointerEvents: "none",
      }}
      aria-hidden="true"
    >
      {/* GPU-accelerated progress fill: full-width div scaled along X.
          Animating transform avoids the layout/paint cost that comes
          with width transitions, so the 600ms tween stays smooth on
          lower-end machines (e.g., the office laptop). */}
      <div
        style={{
          height: "100%",
          width: "100%",
          background: `linear-gradient(90deg, ${C.sageDeep}, ${C.sageHi})`,
          boxShadow: "0 0 16px rgba(77,168,255,0.55)",
          transformOrigin: "left center",
          transform: `scaleX(${pct / 100})`,
          transition: "transform 600ms cubic-bezier(0.2, 0.8, 0.2, 1)",
        }}
      />
    </div>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────────
// All keyframes lifted into a single <style> tag for convenience.

function TourStyles(): JSX.Element {
  return (
    <style>{`
      @keyframes tour-backdrop-in { from { opacity: 0; } to { opacity: 1; } }

      @keyframes tour-orbit-cw  { from { transform: rotate(0deg); }   to { transform: rotate(360deg); } }
      @keyframes tour-orbit-ccw { from { transform: rotate(0deg); }   to { transform: rotate(-360deg); } }

      @keyframes tour-halo-soft {
        0%, 100% { box-shadow: 0 0 18px 1px rgba(242,237,223,0.10); }
        50%      { box-shadow: 0 0 26px 2px rgba(196,122,50,0.30); }
      }
      @keyframes tour-halo-strong {
        0%, 100% { box-shadow: 0 0 22px 2px rgba(77,168,255,0.32); }
        50%      { box-shadow: 0 0 36px 4px rgba(77,168,255,0.50); }
      }

      @keyframes tour-orb-pulse {
        0%, 100% { opacity: 0.85; transform: scale(1); }
        50%      { opacity: 1;    transform: scale(1.06); }
      }

      @keyframes tour-card-enter-fwd {
        from { opacity: 0; transform: perspective(900px) rotateY(-6deg) translateX(20px) scale(0.96); }
        to   { opacity: 1; transform: perspective(900px) rotateY(0)     translateX(0)    scale(1); }
      }
      @keyframes tour-card-enter-back {
        from { opacity: 0; transform: perspective(900px) rotateY(6deg)  translateX(-20px) scale(0.96); }
        to   { opacity: 1; transform: perspective(900px) rotateY(0)     translateX(0)     scale(1); }
      }

      @keyframes tour-intro-fade-in { from { opacity: 0; } to { opacity: 1; } }
      @keyframes tour-intro-orb-in {
        from { opacity: 0; transform: scale(0.7) translateY(20px); filter: blur(12px); }
        to   { opacity: 1; transform: scale(1)   translateY(0);   filter: blur(0); }
      }
      @keyframes tour-intro-line {
        from { opacity: 0; transform: translateY(14px); filter: blur(6px); }
        to   { opacity: 1; transform: translateY(0);    filter: blur(0); }
      }
      @keyframes tour-intro-word {
        from { opacity: 0; transform: translateY(28px); filter: blur(10px); }
        to   { opacity: 1; transform: translateY(0);    filter: blur(0); }
      }
      @keyframes tour-ambient-drift {
        0%   { background-position: 0% 50%; }
        50%  { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      @keyframes tour-cta-breathe {
        0%, 100% { box-shadow: 0 14px 38px rgba(22,163,74,0.40), 0 0 0 0 rgba(196,122,50,0.45); }
        50%      { box-shadow: 0 14px 38px rgba(22,163,74,0.40), 0 0 0 12px rgba(77,168,255,0); }
      }
      @keyframes tour-comet-trail {
        0%   { opacity: 0; transform-origin: 0 50%; transform: rotate(var(--angle, 0deg)) scaleX(0); }
        20%  { opacity: 1; }
        100% { opacity: 0; transform: rotate(var(--angle, 0deg)) scaleX(1); }
      }
      @keyframes tour-comet-echo {
        0%   { opacity: 0; transform-origin: 0 50%; transform: rotate(var(--angle, 0deg)) scaleX(0); }
        15%  { opacity: 0.7; }
        100% { opacity: 0; transform: rotate(var(--angle, 0deg)) scaleX(1); }
      }
      .tour-next-btn:hover {
        transform: translateY(-1px);
        filter: brightness(1.08) saturate(1.1);
        box-shadow: 0 10px 26px rgba(77,168,255,0.48), inset 0 1px 0 rgba(255,255,255,0.28);
      }
      .tour-next-btn:active {
        transform: translateY(0);
        filter: brightness(0.96);
      }
      .tour-back-btn:not(:disabled):hover {
        border-color: rgba(124,194,255,0.45);
        color: #E8E5D9;
        background: rgba(196,122,50,0.06);
      }
      .tour-skip-link:hover {
        color: #C4C2B5;
        text-decoration-color: rgba(124,194,255,0.6);
      }
      .tour-dismiss-btn:hover {
        color: #E8E5D9;
        opacity: 1;
        transform: scale(1.1);
      }
      @keyframes tour-stage-in {
        from { opacity: 0; }
        to   { opacity: 1; }
      }
      @keyframes tour-art-pulse {
        0%, 100% { opacity: 0.85; }
        50%      { opacity: 1; }
      }
      @keyframes tour-art-glow {
        0%, 100% { opacity: 0.5; transform: scale(1); }
        50%      { opacity: 1;   transform: scale(1.06); }
      }
      @keyframes tour-art-slide {
        from { opacity: 0; transform: translateY(8px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @keyframes tour-art-grow {
        from { opacity: 0; transform: scaleY(0.2); }
        to   { opacity: 1; transform: scaleY(1); }
      }
      @keyframes tour-art-shimmer {
        0%   { background-position: 0% 50%; }
        100% { background-position: -220% 50%; }
      }
      @keyframes tour-slide-fade-in {
        from { opacity: 0; transform: translateY(6px); }
        to   { opacity: 1; transform: translateY(0); }
      }
    `}</style>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────

function waitMs(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

async function waitForElement(selector: string, ceiling: number): Promise<Element | null> {
  const start = Date.now();
  while (Date.now() - start < ceiling) {
    const el = document.querySelector(selector);
    if (el) return el;
    await waitMs(50);
  }
  return null;
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function matchesRoutePattern(actual: string, pattern: string): boolean {
  const a = actual.split("/").filter(Boolean);
  const p = pattern.split("/").filter(Boolean);
  if (a.length !== p.length) return false;
  for (let i = 0; i < p.length; i++) {
    const seg = p[i]!;
    if (seg.startsWith(":")) continue;
    if (seg !== a[i]) return false;
  }
  return true;
}

const ROMAN_MAP: ReadonlyArray<readonly [number, string]> = [
  [50, "L"],
  [40, "XL"],
  [10, "X"],
  [9, "IX"],
  [5, "V"],
  [4, "IV"],
  [1, "I"],
];

function romanNumeral(n: number): string {
  let result = "";
  let remaining = Math.max(0, Math.floor(n));
  for (const [value, sym] of ROMAN_MAP) {
    while (remaining >= value) {
      result += sym;
      remaining -= value;
    }
  }
  return result || "·";
}
// useMemo is imported for future use but currently unused — referenced
// here to keep tree-shaking happy when adding new memoized derivations.
void useMemo;
